import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Lazy Sentry loading: @sentry/angular is ~100kB gzipped and only ever runs
 * on live hosts — keeping it out of the initial bundle buys real LCP. The
 * module is fetched once and shared between init (main.ts) and the error
 * handler below.
 */
let _sentry: Promise<typeof import('@sentry/angular')> | undefined;

export function loadSentry(): Promise<typeof import('@sentry/angular')> {
  if (!_sentry) _sentry = import('@sentry/angular');
  return _sentry;
}

function isLiveHost(): boolean {
  const h = window.location.hostname;
  // Production hosts only. advocad.ai is matched EXACTLY (apex + www), not by
  // suffix — staging lives at staging.advocad.ai and carries the same DSN, so a
  // blanket `.advocad.ai` would make staging start reporting as production.
  return (
    h === 'advocad.app' ||
    h.endsWith('.advocad.app') ||
    h === 'advocad.ai' ||
    h === 'www.advocad.ai'
  );
}

/**
 * Console-first error handler that forwards to Sentry on live hosts. Errors
 * thrown before the SDK chunk lands still arrive — captureException queues
 * on the shared module promise.
 */
@Injectable()
export class LazySentryErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    console.error(error);
    if (!isLiveHost()) return;
    void loadSentry().then((Sentry) => {
      Sentry.captureException(
        (error as { originalError?: unknown })?.originalError ?? error,
      );
    });
  }
}
